import {
  minorReleaseDocument,
  minorReleaseFileName,
  type MinorRelease,
} from './minorReleaseDocument';

/**
 * Renders a termo to PDF.
 *
 * Only the DRAWING lives here — what the document says is `minorReleaseDocument`, which is pure
 * and tested. This file is the part a test cannot read, so it is kept as thin as possible: it
 * walks the model and puts it on a page, and makes no decisions of its own about wording,
 * ordering or whether something counts as approved.
 *
 * jsPDF is imported dynamically, the same way the Clientes report does it — it and autotable are
 * several hundred kilobytes that most sessions never need.
 */
export async function downloadMinorReleasePdf(
  release: MinorRelease,
  ctx: { customerName: string; unitLabel?: string },
): Promise<void> {
  const doc = minorReleaseDocument(release, ctx);
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const width = pageW - margin * 2;
  let y = margin;

  /** Starts a new page when the next block would run off this one. */
  const ensure = (needed: number) => {
    if (y + needed > pageH - margin) {
      pdf.addPage();
      y = margin;
      stampWatermark();
    }
  };

  /**
   * Stamped across EVERY page of an unapproved termo.
   *
   * Per page, not once: a reader handed page two of a pending document would otherwise see
   * nothing marking it, and a sheet that looks authorised is exactly what gets waved at a
   * porteiro.
   */
  const stampWatermark = () => {
    if (!doc.watermark) return;
    pdf.saveGraphicsState();
    // @ts-expect-error — GState is present at runtime; jsPDF's types lag its API.
    pdf.setGState(new pdf.GState({ opacity: 0.12 }));
    pdf.setFontSize(46);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(200, 0, 0);
    pdf.text(doc.watermark, pageW / 2, pageH / 2, { align: 'center', angle: 32 });
    pdf.restoreGraphicsState();
    pdf.setTextColor(0, 0, 0);
  };

  stampWatermark();

  // ── Heading ────────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10.5);
  const titleLines = pdf.splitTextToSize(doc.title, width);
  pdf.text(titleLines, margin, y + 4);
  y += titleLines.length * 5 + 4;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(doc.subtitle, margin, y);
  y += 8;

  pdf.setDrawColor(210);
  pdf.line(margin, y, pageW - margin, y);
  y += 7;

  // ── Sections ───────────────────────────────────────────────────────────────
  for (const section of doc.sections) {
    ensure(10 + section.rows.length * 6);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text(section.heading.toUpperCase(), margin, y);
    y += 5;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    for (const [label, value] of section.rows) {
      ensure(6);
      pdf.setTextColor(110);
      pdf.text(`${label}:`, margin, y);
      pdf.setTextColor(0);
      pdf.text(value || '—', margin + 38, y);
      y += 5.5;
    }
    y += 3;
  }

  // ── Declaration ────────────────────────────────────────────────────────────
  y += 2;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  ensure(8);
  pdf.text('TERMO', margin, y);
  y += 5;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9.5);
  for (const paragraph of doc.declaration.split('\n\n')) {
    const lines = pdf.splitTextToSize(paragraph.trim(), width);
    ensure(lines.length * 4.6 + 4);
    pdf.text(lines, margin, y);
    y += lines.length * 4.6 + 3.5;
  }

  // ── Signature ──────────────────────────────────────────────────────────────
  ensure(46);
  y += 6;
  if (doc.signature.image?.startsWith('data:image')) {
    try {
      pdf.addImage(doc.signature.image, 'PNG', margin, y, 62, 22);
    } catch {
      // A corrupt data URI must not take the whole document down — the rest of the termo is
      // still the record, and the missing signature is visible by its absence.
    }
  }
  y += 24;
  pdf.setDrawColor(60);
  pdf.line(margin, y, margin + 78, y);
  y += 4.5;
  pdf.setFontSize(9.5);
  pdf.text(doc.signature.signedBy || '—', margin, y);
  y += 4.5;
  pdf.setFontSize(8.5);
  pdf.setTextColor(110);
  pdf.text('Nome do pai/mãe ou responsável', margin, y);
  y += 4.5;
  if (doc.signature.signedOn) {
    pdf.text(`Assinado em ${doc.signature.signedOn}`, margin, y);
    y += 4.5;
  }

  // ── Decision ───────────────────────────────────────────────────────────────
  ensure(12);
  y += 4;
  pdf.setTextColor(0);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  const decisionLines = pdf.splitTextToSize(doc.decision, width);
  pdf.text(decisionLines, margin, y);

  pdf.save(minorReleaseFileName(release));
}
